import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone, ShoppingBag } from "lucide-react";
import { MaskedLine, FadeUp, SectionHead } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import StatsBand from "@/components/StatsBand";
import { COMPANY, IMAGES, ASSETS, whatsappLink } from "@/data/site";
import { PRODUCTS, BRANDS } from "@/data/products";

const FEATURED = ["special-mixture", "ratlami-sev", "barwani-special-bhujia", "chakli", "masala-peanuts", "banana-chips"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-[100svh] flex items-center overflow-hidden bg-ink">
      <motion.div style={{ y: yImg }} className="absolute inset-y-0 right-0 w-full lg:w-[58%]">
        <motion.img
          src={IMAGES.friedSticks}
          alt="Golden namkeen fresh from the Waghe & Sons production floor"
          className="w-full h-full object-cover opacity-45"
          initial={{ scale: 1.02 }}
          animate={{ scale: 1.16 }}
          transition={{ duration: 22, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <p className="absolute -bottom-10 right-0 font-display text-[26vw] leading-none text-outline-faint select-none pointer-events-none" aria-hidden>
        1965
      </p>

      <div className="absolute bottom-12 right-8 lg:right-14 z-10 hidden md:block" data-testid="hero-spin-badge">
        <div className="relative w-28 h-28 lg:w-32 lg:h-32">
          <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_18s_linear_infinite]">
            <defs>
              <path id="wagheCircle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
            </defs>
            <text style={{ fontSize: "8.4px", letterSpacing: "2.6px", fill: "#D4AF37", textTransform: "uppercase", fontFamily: "Outfit, sans-serif" }}>
              <textPath href="#wagheCircle">Since 1965 · Barwani to the World ·</textPath>
            </text>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display italic text-gold text-xl lg:text-2xl">WS</span>
          </div>
        </div>
      </div>

      <motion.div style={{ y: yText }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full">
        <MaskedLine delay={0.15}>
          <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-overline text-gold border border-gold/30 rounded-full px-5 py-2.5 bg-black/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            Manufacturers of Premium Indian Namkeen &amp; Snacks
          </span>
        </MaskedLine>

        <h1 className="mt-8 font-display tracking-tight leading-[0.98]">
          <MaskedLine delay={0.3}>
            <span className="block text-5xl sm:text-7xl lg:text-8xl text-cream">FROM BARWANI</span>
          </MaskedLine>
          <MaskedLine delay={0.42}>
            <span className="block text-5xl sm:text-7xl lg:text-8xl text-cream">
              TO THE <em className="text-gold italic">WORLD.</em>
            </span>
          </MaskedLine>
        </h1>

        <FadeUp delay={0.65} className="mt-8 max-w-xl">
          <p className="text-base md:text-lg text-zinc-300 font-light leading-relaxed" data-testid="hero-subtext">
            Sixty years of craft. Sixty-plus signature varieties. Pure traditional spices, premium edible oil,
            and a family name that India has trusted since 1965 — now exporting to the world.
          </p>
        </FadeUp>

        <FadeUp delay={0.8} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/products"
            data-testid="hero-explore-products-button"
            className="inline-flex items-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(212,175,55,0.4)] hover:bg-gold-light"
          >
            Explore 60+ Varieties <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/export-inquiry"
            data-testid="hero-partner-button"
            className="inline-flex items-center gap-2.5 border border-gold/60 text-gold text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[transform,background-color,color,border-color] duration-300 hover:-translate-y-1 hover:bg-gold hover:text-ink"
          >
            Become a Distributor <ArrowUpRight className="w-4 h-4" />
          </Link>
        </FadeUp>

        <FadeUp delay={0.95} className="mt-14 flex flex-wrap gap-x-10 gap-y-4">
          {[["Since", "1965"], ["Varieties", "60+"], ["Workforce", "50+"], ["Status", "Export Ready"]].map(([k, v]) => (
            <div key={k}>
              <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">{k}</p>
              <p className="font-display text-xl text-cream mt-1">{v}</p>
            </div>
          ))}
        </FadeUp>
      </motion.div>
    </section>
  );
}

export default function Home() {
  const featured = FEATURED.map((id) => PRODUCTS.find((p) => p.id === id));
  return (
    <>
      <Hero />
      <EditorialMarquee />

      <section className="py-24 sm:py-32 bg-ink" data-testid="home-intro">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div className="lg:sticky lg:top-32">
            <SectionHead
              testId="home-intro-head"
              overline="The House of Waghe"
              title={<>A family recipe,<br />scaled for the world.</>}
            />
          </div>
          <div className="space-y-6 lg:pt-16">
            <FadeUp>
              <p className="text-base md:text-lg text-zinc-400 font-light leading-relaxed">
                What began in our grandfather's time as <span className="text-cream">Waghe Namkeen</span> has grown,
                through three generations of dedication, into {COMPANY.name} — a modern manufacturing and
                export-focused house that never let go of its traditional Indian flavours.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="text-base md:text-lg text-zinc-400 font-light leading-relaxed">
                Today, <span className="text-gold">Waghe Shree</span> serves the Indian market while{" "}
                <span className="text-emerald-light">Tasty Mint</span> carries our craft to global shelves —
                every batch made with carefully selected ingredients, premium edible oil and pure traditional spices.
              </p>
            </FadeUp>
            <FadeUp delay={0.18}>
              <Link to="/about" data-testid="home-story-link" className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-[0.16em] border-b border-gold/40 pb-1.5 hover:gap-4 transition-[gap] duration-300">
                Read Our Story <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <StatsBand />

      <section className="py-24 sm:py-32 bg-ink" data-testid="home-brands">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead
            testId="home-brands-head"
            overline="Two Brands · One Legacy"
            title="Waghe Shree for India. Tasty Mint for the world."
          />
          <div className="mt-16 grid lg:grid-cols-2 gap-6">
            {[BRANDS.shree, BRANDS.mint].map((b, i) => (
              <FadeUp key={b.name} delay={i * 0.12}>
                <Link
                  to="/brands"
                  data-testid={`home-brand-card-${b.accent}`}
                  className={`group relative block overflow-hidden rounded-sm border border-white/10 bg-coal p-8 sm:p-12 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 ${
                    b.accent === "maroon" ? "hover:border-maroon-light/60" : "hover:border-emerald-light/60"
                  } hover:shadow-[0_24px_60px_rgba(0,0,0,0.5)]`}
                >
                  <div className={`absolute top-0 left-0 w-full h-1 ${b.accent === "maroon" ? "bg-maroon-light" : "bg-emerald-light"}`} />
                  <div className="bg-white rounded-sm p-5 inline-block">
                    <img
                      src={b.accent === "maroon" ? ASSETS.logoShree : ASSETS.logoMint}
                      alt={`${b.name} logo`}
                      className="h-24 sm:h-28 w-auto"
                    />
                  </div>
                  <p className="mt-7 text-[11px] uppercase tracking-overline text-zinc-500">{b.market}</p>
                  <h3 className="mt-2 font-display text-3xl sm:text-4xl text-cream">{b.name}</h3>
                  <ul className="mt-6 space-y-2.5">
                    {b.points.slice(0, 3).map((pt) => (
                      <li key={pt} className="flex gap-3 text-sm text-zinc-400 font-light">
                        <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0 mt-1.5" /> {pt}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-8 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.18em] group-hover:gap-4 transition-[gap] duration-300">
                    Discover the brand <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-coal border-y border-white/10" data-testid="home-featured-products">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHead
              testId="home-products-head"
              overline="Signature Selection"
              title="Crafted with pure spices & premium oil."
            />
            <FadeUp delay={0.2}>
              <Link to="/products" data-testid="home-view-all-products" className="inline-flex items-center gap-2 border border-gold/60 text-gold text-xs font-semibold uppercase tracking-[0.16em] px-6 py-3.5 rounded-sm transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink">
                View All 24 Products <ArrowUpRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featured.map((p, i) => (
              <FadeUp key={p.id} delay={(i % 3) * 0.1}>
                <Link
                  to="/products"
                  data-testid={`featured-product-${p.id}`}
                  className="group block bg-ink border border-white/10 rounded-sm overflow-hidden transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-gold/50 hover:shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                    <span className={`absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${p.brand === "Tasty Mint" ? "bg-emerald text-emerald-100 border border-emerald-light/50" : "bg-maroon text-red-100 border border-maroon-light/50"}`}>
                      {p.brand}
                    </span>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-gold">{p.category}</p>
                    <h3 className="mt-1.5 font-display text-lg sm:text-xl text-cream leading-snug">{p.name}</h3>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ink" data-testid="home-story-banner">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <div className="image-frame border border-gold/30">
              <img src={ASSETS.banner} alt="Waghe & Sons factory, brands and product range" className="w-full h-auto" data-testid="home-corporate-banner" />
            </div>
          </FadeUp>
          <div>
            <SectionHead
              testId="home-story-head"
              overline="Our Story · Since 1965"
              title="From a family namkeen house to a modern export enterprise."
              description="Nearly sixty years, three generations, one uncompromising standard. Meet the family and the factory behind Barwani's most trusted namkeen."
            />
            <FadeUp delay={0.25}>
              <Link to="/about" data-testid="home-about-cta" className="mt-9 inline-flex items-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(212,175,55,0.35)]">
                Our Heritage <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <EditorialMarquee variant="dark" testId="editorial-marquee-dark" />

      <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="home-export-cta">
        <img src={IMAGES.exportContainers} alt="Export containers ready for global dispatch" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-overline text-gold mb-5">Export &amp; Distribution</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="font-display text-4xl sm:text-6xl tracking-tight leading-[1.04] text-cream max-w-3xl">
              Made in India.<br /><em className="text-gold italic">Served Worldwide.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="mt-6 max-w-xl text-base md:text-lg text-zinc-300 font-light leading-relaxed">
              Distributor territories open across India. Export partnerships welcome worldwide — private labelling,
              bulk supply and full documentation support under the Tasty Mint brand.
            </p>
          </FadeUp>
          <FadeUp delay={0.24} className="mt-10 flex flex-wrap gap-4">
            <Link to="/export-inquiry" data-testid="home-export-cta-button" className="inline-flex items-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(212,175,55,0.4)]">
              Dealer &amp; Export Enquiry <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a href={whatsappLink("Namaste! I want to place a bulk / online order for Waghe Shree namkeen.")} target="_blank" rel="noopener noreferrer" data-testid="home-order-online-button" className="inline-flex items-center gap-2.5 border border-white/25 text-cream text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[border-color,color,transform] duration-300 hover:-translate-y-1 hover:border-gold hover:text-gold">
              <ShoppingBag className="w-4 h-4" /> Order on WhatsApp
            </a>
            <a href={`tel:${COMPANY.phoneRaw[0]}`} data-testid="home-call-button" className="inline-flex items-center gap-2.5 border border-white/25 text-cream text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[border-color,color,transform] duration-300 hover:-translate-y-1 hover:border-gold hover:text-gold">
              <Phone className="w-4 h-4" /> {COMPANY.phones[0]}
            </a>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
